import React, { memo, MouseEvent, SyntheticEvent } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import Badge from '@mui/material/Badge';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import PaletteIcon from '@mui/icons-material/Palette';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import useLocales from 'src/hooks/useLocales';

import ColorPicker from 'src/components/color-picker/ColorPicker';

import { FONT_SIZES } from '../constants/elementSettingsConstants';
import { EL_FONT_FAMILY } from '../../../../../@types/forms/constants/ElementSettings';
import { ElementTextStyleProps, ElementTextDecorationProps } from '../utils/elementSettingsUtils';
import {
  TextStyle,
  FontFamily,
  TextAlignment,
} from '../../../../../@types/forms/enums/ElementProperties';

type ElementTextStyleSettingsProps = {
  font: FontFamily;
  fontSize: number;
  textAlignment: TextAlignment;
  elementTextStyle: ElementTextStyleProps | null;
  elementTextDecoration: ElementTextDecorationProps | null;
  textRotation: number | string | Array<number | string> | null;
  selectedColor: string | null;
  isOpenTextRotation: boolean;
  isOpenColor: boolean;
  onFontChange: (font: FontFamily) => void;
  onFontSizeChange: (fontSize: number) => void;
  onAlignmentChange: (alignment: TextAlignment) => void;
  onTextStyleChange: (style: TextStyle) => void;
  onTextDecorationChange: () => void;
  onSliderChange: (event: Event, newValue: number | number[]) => void;
  onTextRotationInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTextRotationBlur: () => void;
  onTextColorChange: (color: any) => void;
  onToggleTextRotation: () => void;
  onToggleColor: () => void;
};

const ElementTextStyleSettings: React.FC<ElementTextStyleSettingsProps> = ({
  font,
  fontSize,
  textAlignment,
  elementTextStyle,
  elementTextDecoration,
  textRotation,
  selectedColor,
  isOpenTextRotation,
  isOpenColor,
  onFontChange,
  onFontSizeChange,
  onAlignmentChange,
  onTextStyleChange,
  onTextDecorationChange,
  onSliderChange,
  onTextRotationInputChange,
  onTextRotationBlur,
  onTextColorChange,
  onToggleTextRotation,
  onToggleColor,
}) => {
  const { t } = useLocales();

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Autocomplete
          value={font || EL_FONT_FAMILY[0]}
          onChange={(event: SyntheticEvent<Element, Event>, value: FontFamily) => {
            if (value !== null) {
              onFontChange(value);
            }
          }}
          sx={{ flexGrow: 1 }}
          disableClearable
          fullWidth
          size="small"
          options={EL_FONT_FAMILY}
          renderInput={(params) => (
            <TextField {...params} label={t('forms.elements.settings.font')} id="zyj3mjxp" />
          )}
          popupIcon={<ExpandMoreRoundedIcon />}
          id="qk3RYn"
        />

        <Autocomplete
          value={fontSize || 11}
          onChange={(event: SyntheticEvent<Element, Event>, value: number) => {
            if (value !== null) {
              onFontSizeChange(value);
            }
          }}
          sx={{ width: '100px' }}
          disableClearable
          size="small"
          options={Array.from(FONT_SIZES)}
          getOptionLabel={(option) => option.toString()}
          renderInput={(params) => (
            <TextField {...params} label={t('forms.elements.settings.fontSize')} id="ixnu827a" />
          )}
          popupIcon={<ExpandMoreRoundedIcon />}
          id="G6qpLX"
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <ToggleButtonGroup
          value={textAlignment}
          exclusive
          size="small"
          sx={{ display: 'flex', justifyContent: 'space-evenly' }}
          onChange={(event: MouseEvent<HTMLElement>, value: TextAlignment) => onAlignmentChange(value)}
        >
          <ToggleButton value={TextAlignment.Left}>
            <FormatAlignLeftIcon id="FormatAlignLeftIcon" />
          </ToggleButton>

          <ToggleButton value={TextAlignment.Center}>
            <FormatAlignCenterIcon id="FormatAlignCenterIcon" />
          </ToggleButton>

          <ToggleButton value={TextAlignment.Right}>
            <FormatAlignRightIcon id="FormatAlignRightIcon" />
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup size="small" sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <ToggleButton
            value={TextStyle.Bold}
            onClick={() => onTextStyleChange(TextStyle.Bold)}
            selected={elementTextStyle ? elementTextStyle.bold : false}
          >
            <FormatBoldIcon id="FormatBoldIcon" />
          </ToggleButton>

          <ToggleButton
            value={TextStyle.Italic}
            onClick={() => onTextStyleChange(TextStyle.Italic)}
            selected={elementTextStyle ? elementTextStyle.italic : false}
          >
            <FormatItalicIcon id="FormatItalicIcon" />
          </ToggleButton>

          <ToggleButton
            value="underlined"
            onClick={onTextDecorationChange}
            selected={elementTextDecoration ? elementTextDecoration.underline : false}
          >
            <FormatUnderlinedIcon id="FormatUnderlinedIcon" />
          </ToggleButton>

          <ToggleButton value="rotate" disabled onClick={onToggleTextRotation}>
            <Badge badgeContent={textRotation} max={999} color="primary">
              <RotateRightIcon />
            </Badge>
          </ToggleButton>

          <ToggleButton value="color" aria-label="color" onClick={onToggleColor}>
            <Badge
              variant="dot"
              sx={{
                '& .MuiBadge-badge': {
                  color: selectedColor,
                  backgroundColor: selectedColor,
                },
              }}
            >
              <PaletteIcon id="PaletteIcon" />
            </Badge>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ROTATE */}
      <Box display={isOpenTextRotation ? 'block' : 'none'}>
        <Typography id="input-slider" gutterBottom variant="caption">
          Rotate Text
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ px: 1 }}>
          <Grid size="grow">
            <Slider
              max={360}
              value={typeof textRotation === 'number' ? textRotation : 0}
              onChange={onSliderChange}
              aria-labelledby="input-slider"
            />
          </Grid>
          <Grid>
            <Input
              value={textRotation}
              size="small"
              onChange={onTextRotationInputChange}
              onBlur={onTextRotationBlur}
              inputProps={{
                step: 1,
                min: 0,
                max: 360,
                type: 'number',
                'aria-labelledby': 'input-slider',
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* COLOR */}
      <Box
        sx={[
          {
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          },
          isOpenColor
            ? {
                display: 'flex',
              }
            : {
                display: 'none',
              },
        ]}
      >
        <ColorPicker color={selectedColor || '#221271'} setColor={onTextColorChange} spacing={2} />
      </Box>
    </>
  );
};

export default memo(ElementTextStyleSettings);
